import {
  CampaignTimelineStage,
  CampaignTimelineStageId,
  DeliverableStatus,
  PlacedStage,
  ProgressKind,
  StageLabel,
} from "../types";
import { addDays, daysBetween, parseISODate, startOfDay, toISODate } from "./dateUtils";

export const STAGE_META: Record<string, { label: string; description: string }> = {
  brief: {
    label: "Brief",
    description: "Share requirements and kick off the creator.",
  },
  script: {
    label: "Script",
    description: "Creator submits a script or concept for review.",
  },
  draft: {
    label: "Draft",
    description: "Creator submits a content draft for review.",
  },
  live: {
    label: "Live",
    description: "Approved content is published.",
  },
};

export const DEFAULT_TIMELINE_STAGES: CampaignTimelineStage[] = [
  { id: "brief", label: "Brief", enabled: true, brandApprover: false },
  { id: "script", label: "Script", enabled: true, brandApprover: true },
  { id: "draft", label: "Draft", enabled: true, brandApprover: true },
  { id: "live", label: "Live", enabled: true, brandApprover: false },
];

export function cloneDefaultTimeline(): CampaignTimelineStage[] {
  return DEFAULT_TIMELINE_STAGES.map((s) => ({ ...s }));
}

export function getStageStatuses(
  status: DeliverableStatus | undefined
): Record<string, StageLabel> {
  const stages: Record<string, StageLabel> = {
    brief: "-",
    script: "-",
    draft: "-",
    live: "-",
  };
  if (!status) return stages;

  const st = status.toLowerCase();
  if (
    st.includes("cancel") ||
    st.includes("decline") ||
    st.includes("withdraw")
  ) {
    stages.brief = stages.script = stages.draft = stages.live = "Cancelled";
    return stages;
  }

  const setCompleted = (keys: string[]) => {
    keys.forEach((k) => {
      stages[k] = "Completed";
    });
  };

  switch (status) {
    case "cancelled":
      stages.brief = stages.script = stages.draft = stages.live = "Cancelled";
      break;
    case "brief_pending":
      stages.brief = "Pending";
      break;
    case "script_pending":
      setCompleted(["brief"]);
      stages.script = "Pending";
      break;
    case "script_internal_review":
      setCompleted(["brief"]);
      stages.script = "Internal review";
      break;
    case "script_approval":
      setCompleted(["brief"]);
      stages.script = "Approval Pending";
      break;
    case "script_changes":
      setCompleted(["brief"]);
      stages.script = "Requested Changes";
      break;
    case "draft_pending":
      setCompleted(["brief", "script"]);
      stages.draft = "Pending";
      break;
    case "draft_internal_review":
      setCompleted(["brief", "script"]);
      stages.draft = "Internal review";
      break;
    case "draft_approval":
      setCompleted(["brief", "script"]);
      stages.draft = "Approval Pending";
      break;
    case "draft_changes":
      setCompleted(["brief", "script"]);
      stages.draft = "Requested Changes";
      break;
    case "live_pending":
      setCompleted(["brief", "script", "draft"]);
      stages.live = "Pending";
      break;
    case "live_completed":
      setCompleted(["brief", "script", "draft", "live"]);
      break;
    default:
      stages.brief = "Pending";
      break;
  }
  return stages;
}

export function progressKind(label: StageLabel): ProgressKind {
  if (label === "Completed") return "done";
  if (label === "Cancelled") return "cancelled";
  if (label === "Requested Changes") return "blocked";
  if (label === "-" || !label) return "upcoming";
  return "active";
}

export function resolveStageProgressKind(params: {
  status?: string;
  fallbackLabel?: StageLabel;
  startDate?: string | Date;
  isCancelled?: boolean;
}): ProgressKind {
  if (params.isCancelled) return "cancelled";

  const st = (params.status || "").toLowerCase().trim();

  // 1. Cancelled
  if (
    st.includes("cancel") ||
    st.includes("decline") ||
    st.includes("withdraw") ||
    params.fallbackLabel === "Cancelled"
  ) {
    return "cancelled";
  }

  // 2. Future start date is Upcoming
  if (params.startDate) {
    const start =
      typeof params.startDate === "string"
        ? parseISODate(params.startDate)
        : params.startDate;
    const today = startOfDay(new Date());
    if (start && start.getTime() > today.getTime()) {
      return "upcoming";
    }
  }

  // 3. Earlier status logic for today or past
  if (params.status) {
    if (st.includes("complete") || st.includes("approve")) {
      return "done";
    }
    if (st.includes("edit") || st.includes("change")) {
      return "blocked";
    }
    return "active";
  }

  return progressKind(params.fallbackLabel || "-");
}

export function spreadStagesEvenly(
  stages: CampaignTimelineStage[],
  rangeStart: Date,
  rangeEnd: Date
): CampaignTimelineStage[] {
  const enabled = stages.filter((s) => s.enabled);
  if (enabled.length === 0) return stages.map((s) => ({ ...s }));

  const totalDays = Math.max(daysBetween(rangeStart, rangeEnd) + 1, enabled.length);
  const baseDays = Math.floor(totalDays / enabled.length);
  const remainder = totalDays % enabled.length;

  let cursor = rangeStart;
  const dated = new Map<CampaignTimelineStageId, { startDate: string; endDate: string }>();

  enabled.forEach((stage, i) => {
    const isLast = i === enabled.length - 1;
    const daysForStage = isLast
      ? Math.max(1, daysBetween(cursor, rangeEnd) + 1)
      : Math.max(1, baseDays + (i < remainder ? 1 : 0));
    const start = cursor;
    const end = isLast ? rangeEnd : addDays(start, Math.max(daysForStage - 1, 0));
    const safeEnd = end < start ? start : end;
    dated.set(stage.id, {
      startDate: toISODate(start),
      endDate: toISODate(safeEnd),
    });
    cursor = addDays(safeEnd, 1);
  });

  return stages.map((s) => {
    const next = dated.get(s.id);
    return next ? { ...s, ...next } : { ...s, startDate: undefined, endDate: undefined };
  });
}

export const DEFAULT_STAGE_DURATIONS: Record<string, number> = {
  brief: 5,
  script: 7,
  draft: 7,
  live: 5,
};

export function applyStageDurations(
  stages: CampaignTimelineStage[],
  rangeStart: Date,
  durations: Record<string, number>
): CampaignTimelineStage[] {
  const enabled = stages.filter((s) => s.enabled);
  if (enabled.length === 0) return stages.map((s) => ({ ...s }));

  let cursor = rangeStart;
  const dated = new Map<string, { startDate: string; endDate: string }>();

  enabled.forEach((stage) => {
    const rawId = (stage.id || "").toLowerCase();
    const rawLabel = (stage.label || "").toLowerCase();
    let stageKey = "draft";
    if (rawId.includes("brief") || rawLabel.includes("brief")) {
      stageKey = "brief";
    } else if (rawId.includes("script") || rawLabel.includes("script")) {
      stageKey = "script";
    } else if (rawId.includes("live") || rawLabel.includes("live")) {
      stageKey = "live";
    } else if (rawId.includes("draft") || rawLabel.includes("draft")) {
      stageKey = "draft";
    }

    const days = Math.max(
      1,
      Math.round(durations[stageKey] ?? DEFAULT_STAGE_DURATIONS[stageKey] ?? 7)
    );
    const start = cursor;
    const end = addDays(start, days - 1);

    dated.set(stage.id, {
      startDate: toISODate(start),
      endDate: toISODate(end),
    });
    cursor = addDays(end, 1);
  });

  return stages.map((s) => {
    const next = dated.get(s.id);
    return next ? { ...s, ...next } : { ...s, startDate: undefined, endDate: undefined };
  });
}

/**
 * Shifts only the targeted single stage by deltaDays, bounded by rangeStart and rangeEnd.
 * This fixes the issue in the original component where dragging one stage shifted all stages.
 */
export function shiftSingleStageDates(
  stages: CampaignTimelineStage[],
  stageId: CampaignTimelineStageId,
  deltaDays: number,
  rangeStart: Date,
  rangeEnd: Date
): CampaignTimelineStage[] {
  if (!deltaDays) return stages.map((s) => ({ ...s }));

  return stages.map((s) => {
    if (s.id !== stageId || !s.enabled) return { ...s };
    const start = parseISODate(s.startDate);
    const end = parseISODate(s.endDate);
    if (!start || !end) return { ...s };

    const duration = daysBetween(start, end);
    let nextStart = addDays(start, deltaDays);
    let nextEnd = addDays(end, deltaDays);

    if (nextStart < rangeStart) {
      nextStart = rangeStart;
      nextEnd = addDays(nextStart, duration);
    }
    if (nextEnd > rangeEnd) {
      nextEnd = rangeEnd;
      nextStart = addDays(nextEnd, -duration);
      if (nextStart < rangeStart) {
        nextStart = rangeStart;
      }
    }

    return {
      ...s,
      startDate: toISODate(nextStart),
      endDate: toISODate(nextEnd),
    };
  });
}

/**
 * Resizes a stage by moving either its start or end edge, clamping appropriately.
 */
export function resizeStageDates(
  stages: CampaignTimelineStage[],
  id: CampaignTimelineStageId,
  edge: "start" | "end",
  deltaDays: number,
  rangeStart: Date,
  rangeEnd: Date
): CampaignTimelineStage[] {
  if (!deltaDays) return stages.map((s) => ({ ...s }));

  return stages.map((s) => {
    if (s.id !== id || !s.enabled) return { ...s };
    const start = parseISODate(s.startDate);
    const end = parseISODate(s.endDate);
    if (!start || !end) return { ...s };

    let nextStart = start;
    let nextEnd = end;
    if (edge === "start") {
      nextStart = addDays(start, deltaDays);
      if (nextStart < rangeStart) nextStart = rangeStart;
      if (nextStart > end) nextStart = end;
    } else {
      nextEnd = addDays(end, deltaDays);
      if (nextEnd > rangeEnd) nextEnd = rangeEnd;
      if (nextEnd < start) nextEnd = start;
    }
    return {
      ...s,
      startDate: toISODate(nextStart),
      endDate: toISODate(nextEnd),
    };
  });
}

/**
 * Converts stages to PlacedStage objects with percentages for CSS positioning.
 * Only places stages that have valid startDate and endDate, avoiding artificial equal splits.
 */
export function placeStages(
  stages: CampaignTimelineStage[],
  statuses: Record<string, StageLabel>,
  rangeStart: Date,
  rangeEnd: Date,
  isParentCancelled?: boolean
): PlacedStage[] {
  const enabled = stages.filter((s) => s.enabled);
  const totalDays = Math.max(daysBetween(rangeStart, rangeEnd) + 1, 1);

  // Only place stages that actually have both valid startDate and endDate
  const datedStages = enabled.filter((s) => {
    if (!s.startDate || !s.endDate) return false;
    const start = parseISODate(s.startDate);
    const end = parseISODate(s.endDate);
    return Boolean(start && end);
  });

  return datedStages
    .map((s): PlacedStage | null => {
      const fallbackLabel = statuses[s.id];
      const start = parseISODate(s.startDate)!;
      const end = parseISODate(s.endDate)!;

      const isCancelled = Boolean(
        isParentCancelled ||
        s.isCancelled ||
        fallbackLabel === "Cancelled"
      );

      const kind = resolveStageProgressKind({
        status: s.status,
        fallbackLabel,
        startDate: start,
        isCancelled,
      });

      const label = s.label || STAGE_META[s.id]?.label || s.id.toUpperCase();

      // Skip stages completely outside the viewable range
      if (end < rangeStart || start > rangeEnd) {
        return null;
      }

      const clampedStart = start < rangeStart ? rangeStart : start;
      const clampedEnd = end > rangeEnd ? rangeEnd : end;
      const leftDays = Math.max(0, daysBetween(rangeStart, clampedStart));
      const spanDays = Math.max(1, daysBetween(clampedStart, clampedEnd) + 1);
      const leftPct = (leftDays / totalDays) * 100;
      const widthPct = Math.min((spanDays / totalDays) * 100, 100 - leftPct);

      return {
        id: s.id,
        ...(s.taskId ? { taskId: s.taskId } : {}),
        label,
        kind,
        startDate: toISODate(start),
        endDate: toISODate(end),
        leftPct,
        widthPct,
        ...(isCancelled ? { isCancelled: true } : {}),
      };
    })
    .filter((s): s is PlacedStage => s !== null);
}

export function applyDragToStages(
  stages: CampaignTimelineStage[],
  mode: "move" | "resize-start" | "resize-end",
  stageId: CampaignTimelineStageId,
  dayDelta: number,
  rangeStart: Date,
  rangeEnd: Date
): CampaignTimelineStage[] {
  if (mode === "move") {
    return shiftSingleStageDates(stages, stageId, dayDelta, rangeStart, rangeEnd);
  }
  return resizeStageDates(
    stages,
    stageId,
    mode === "resize-start" ? "start" : "end",
    dayDelta,
    rangeStart,
    rangeEnd
  );
}
